/* eslint-disable react/no-array-index-key */
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Avatar from '@deprecated-ui/common/Avatar';
import { useDispatch, useSelector } from 'react-redux';
import chatSlice, {
  selectors as chatSelectors,
  effects as chatEffects,
} from 'lib/entities/chat/slice';
import { useMemo, useState } from 'react';
import { handleErrorAvatar } from 'utils/general';
import VisibilitySensor from 'react-visibility-sensor';
import Image from 'global/Image';
import { Autocomplete, SectionWrapper } from '../styled';
import ReceiverItem from './ReceiverItem';
import {
  transformRoomToReceiverSchema,
  transformSearchToReceiverSchema,
} from '../utils';

const TOTAL_ROOM_PER_PAGE = 25;

interface SearchReceiverProps {
  selectedReceiverId: Array<string | number>;
  onSelectReceiver: (receiver: any, id: number) => void;
}

function SearchReceiver({
  selectedReceiverId,
  onSelectReceiver,
}: SearchReceiverProps) {
  const dispatch = useDispatch();

  const [timeInterval, setTimeInterval] = useState<any>(0);
  const [selectedReceiver, setSelectedReceiver] =
    useState<Record<string, any> | null>(null);
  const [notContactReceivers, setNotContactReceivers] = useState([]);
  const [isLastPage, setIsLastPage] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const {
    rooms: roomReceivers,
    receivers: { list: receiverList },
    isLoadMoreRooms,
  } = useSelector(chatSelectors);

  const combinedReceivers = useMemo(
    () => [
      ...transformSearchToReceiverSchema(notContactReceivers),
      ...transformRoomToReceiverSchema(roomReceivers),
    ],
    [roomReceivers, notContactReceivers],
  );

  // Search Receiver
  const handleSearch = (val) => {
    // TODO: set to render last contacted users on clear search
    if (val.length === 0) {
      setSelectedReceiver(null);
      dispatch(chatSlice.actions.resetReceiverList());
    }

    if (val.length > 0) {
      if (timeInterval) {
        clearTimeout(timeInterval);
      }

      setTimeInterval(
        setTimeout(() => {
          // @ts-ignore
          dispatch(chatEffects.getSearchReceivers({ keyword: val }));
        }, 1500),
      );
    }
  };

  // Select autocomplete option
  const handleSelectAutocomplete = (val) => {
    // val value format is id-index-type, we do this
    // because in staging we found multiple user that have same id
    const parsedVal = val.split('-');
    const selected = receiverList.find(
      (receiver) => receiver.id === Number(parsedVal[0]),
    );
    setSelectedReceiver(selected);

    // Reset receiver list
    dispatch(chatSlice.actions.resetReceiverList());
  };

  // Select Receiver Item
  const handleSelectReceiver = (receiver) => {
    onSelectReceiver(receiver, receiver.id);
  };

  // Select search result item
  const handleSelectSearchedReceiver = (receiver) => {
    // check if the receiver available in room list
    const roomIdx = roomReceivers.findIndex((room) => {
      if (room.type === 'ROOM_TYPE_PERSONAL') {
        return room.attributes?.personal?.user_id === receiver.id;
      }
      return room.id === receiver.id;
    });
    // check if the receiver available in notContactReceiver state
    const notContactIdx = notContactReceivers.findIndex(
      (room) => room.id === receiver.id,
    );

    if (notContactIdx < 0 && roomIdx < 0) {
      setNotContactReceivers((prev) => [...prev, receiver]);
    } else {
      const prevNotContactReceivers = Array.from(notContactReceivers);
      prevNotContactReceivers.splice(notContactIdx, 1);
      setNotContactReceivers(prevNotContactReceivers);
    }

    onSelectReceiver(receiver, receiver.id);
  };

  const handleLoadMoreRooms = async (isVisible: boolean) => {
    if (isVisible && !isLastPage && !isLoadMoreRooms) {
      const newOffset = roomReceivers.length;

      const getData = await dispatch(
        chatEffects.getRooms({
          limit: TOTAL_ROOM_PER_PAGE,
          offset: newOffset,
          type: 'LIST_TYPE_FORWARD',
        }),
      );

      // @ts-ignore
      const { error, data } = getData.payload;

      if (!error) {
        if (data?.rooms?.length < TOTAL_ROOM_PER_PAGE) {
          setIsLastPage(true);
        }
      }
    }
  };

  const handleChangeAutocomplete = (value) => {
    setSelectedReceiver(null);
    setSearchKeyword(value);
  };

  const searchValue = selectedReceiver?.info?.name || searchKeyword;

  // Search Result Autocomplete Option
  const options = receiverList.map((receiver, index) => ({
    // keep the value is unique
    value: `${receiver.id}-${index}-${receiver.type}`,
    label: (
      <Flex
        align="center"
        key={`${receiver.type}-${receiver.id}-${index}-autocomplete`}
      >
        {/* @ts-expect-error */}
        <Avatar size="x-small" mr="12px">
          <Image
            alt={receiver.info.name}
            src={receiver.info.avatar_url}
            onError={(e) => handleErrorAvatar(e, receiver.info.name)}
          />
        </Avatar>
        <Text family="bold">
          {receiver.info.name} - {receiver.info.description}
        </Text>
      </Flex>
    ),
  }));

  return (
    <>
      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <Autocomplete
          placeholder="Search"
          onSearch={handleSearch}
          onSelect={handleSelectAutocomplete}
          onChange={handleChangeAutocomplete}
          options={options}
          value={searchValue}
          allowClear
        />
      </div>
      {/* @ts-expect-error */}
      <Flex justify="space-between" width="100%" mb="5px">
        <Text family="bold">Send to</Text>
        <Text>{selectedReceiverId.length} of 20 selected</Text>
      </Flex>
      <SectionWrapper>
        {selectedReceiver ? (
          <ReceiverItem
            receiver={{
              name: selectedReceiver.info.name,
              avatar: selectedReceiver.info.avatar_url,
              id: selectedReceiver.id,
              shortName: selectedReceiver.info?.shortened_name,
              type: selectedReceiver.type,
            }}
            onSelect={() => handleSelectSearchedReceiver(selectedReceiver)}
            checked={selectedReceiverId.includes(selectedReceiver.id)}
            disabled={selectedReceiverId.length >= 20}
          />
        ) : (
          <>
            {combinedReceivers.map((receiver, index) => (
              <ReceiverItem
                key={`${receiver.type}-${receiver.id}-${index}-receiver`}
                receiver={{
                  name: receiver.name,
                  avatar: receiver.avatar,
                  id: receiver.id,
                  shortName: receiver.shortName,
                  type: receiver.type,
                }}
                onSelect={() => handleSelectReceiver(receiver)}
                checked={selectedReceiverId.includes(receiver.id)}
                disabled={selectedReceiverId.length >= 20}
              />
            ))}
            {isLoadMoreRooms && <Text align="center">Loading...</Text>}
            <VisibilitySensor onChange={handleLoadMoreRooms}>
              <div>&nbsp;</div>
            </VisibilitySensor>
          </>
        )}
      </SectionWrapper>
    </>
  );
}

export default SearchReceiver;
